import { LOCALE_LABELS, LOCALES, Markdown, RADIUS } from "@elyxeer/ui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { type FormEvent, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { CoverField } from "~/components/covers/CoverField";
import type { CoverPublishing } from "~/components/covers/use-cover-publishing";
import { RecordingPicker } from "~/components/events/RecordingPicker";
import { useLocale } from "~/i18n";
import { fromDateTimeLocal, toDateTimeLocal } from "~/lib/datetime";
import { slugify } from "~/lib/slug";
import type { CoverContext } from "~/types/covers-admin";
import type {
  EventAdmin,
  EventAdminCreate,
  EventExternalKind,
} from "~/types/events-admin";

import { EventReminders } from "./EventReminders";

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const RESERVED_SLUGS = new Set(["featured", "upcoming"]);

interface EventFormProps {
  initial?: EventAdmin;
  busy: boolean;
  errorKey: string | null;
  submitLabel: string;
  onSubmit: (event: EventAdminCreate) => void;
  actions?: ReactNode;
  /** Uploads and generates covers from the form's unsaved fields. */
  covers?: CoverPublishing;
  /** Rendered at the top of the right column — the status panel, on a saved event. */
  aside?: ReactNode;
}

/** Calendar editor with a member-facing Markdown preview. */
export function EventForm({
  initial,
  busy,
  errorKey,
  submitLabel,
  onSubmit,
  actions,
  covers,
  aside,
}: EventFormProps) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const [locale, setLocale] = useState(initial?.locale ?? "cs");
  const [title, setTitle] = useState(initial?.title ?? "");
  const [slug, setSlug] = useState(initial?.slug ?? "");
  const [slugTouched, setSlugTouched] = useState(initial !== undefined);
  const [summary, setSummary] = useState(initial?.summary ?? "");
  const [body, setBody] = useState(initial?.body ?? "");
  const [startsAt, setStartsAt] = useState(
    toDateTimeLocal(initial?.starts_at ?? new Date().toISOString()),
  );
  const [startsAtChanged, setStartsAtChanged] = useState(false);
  const [endsAt, setEndsAt] = useState(
    initial?.ends_at ? toDateTimeLocal(initial.ends_at) : "",
  );
  const [endsAtChanged, setEndsAtChanged] = useState(false);
  const [location, setLocation] = useState(initial?.location ?? "");
  const [externalUrl, setExternalUrl] = useState(initial?.external_url ?? "");
  const [externalKind, setExternalKind] = useState<EventExternalKind | null>(
    initial?.external_kind ?? null,
  );
  const [coverImageUrl, setCoverImageUrl] = useState(
    initial?.cover_image_url ?? "",
  );
  const [streamUrl, setStreamUrl] = useState(initial?.stream_url ?? "");
  // On for a new event: a webinar is what the calendar is for. An existing row keeps
  // whatever it was saved with — the migration left every old row off.
  const [registrationEnabled, setRegistrationEnabled] = useState(
    initial?.registration_enabled ?? true,
  );
  const [registrationCapacity, setRegistrationCapacity] = useState(
    initial?.registration_capacity === null ||
      initial?.registration_capacity === undefined
      ? ""
      : String(initial.registration_capacity),
  );
  const [recordingSlug, setRecordingSlug] = useState<string | null>(
    initial?.recording_slug ?? null,
  );
  const [isPublished, setIsPublished] = useState(initial?.is_published ?? true);
  const [remindersEnabled, setRemindersEnabled] = useState(
    initial?.reminders_enabled ?? true,
  );

  const reservedSlug = RESERVED_SLUGS.has(slug);
  const slugInvalid =
    slug.length > 0 && (!SLUG_PATTERN.test(slug) || reservedSlug);
  const endInvalid =
    endsAt.length > 0 &&
    startsAt.length > 0 &&
    new Date(endsAt).getTime() <= new Date(startsAt).getTime();
  const trimmedExternalUrl = externalUrl.trim();
  let externalUrlInvalid = false;
  if (trimmedExternalUrl) {
    try {
      const parsed = new URL(trimmedExternalUrl);
      externalUrlInvalid =
        parsed.protocol !== "http:" && parsed.protocol !== "https:";
    } catch {
      externalUrlInvalid = true;
    }
  }
  const externalPairInvalid =
    externalUrlInvalid || Boolean(trimmedExternalUrl) !== Boolean(externalKind);
  const trimmedStreamUrl = streamUrl.trim();
  let streamUrlInvalid = false;
  if (trimmedStreamUrl) {
    try {
      const parsed = new URL(trimmedStreamUrl);
      streamUrlInvalid =
        parsed.protocol !== "http:" && parsed.protocol !== "https:";
    } catch {
      streamUrlInvalid = true;
    }
  }
  const capacityNumber =
    registrationCapacity.trim() === "" ? null : Number(registrationCapacity);
  const capacityInvalid =
    capacityNumber !== null &&
    (!Number.isInteger(capacityNumber) || capacityNumber < 1);

  const changeTitle = (next: string) => {
    setTitle(next);
    if (!slugTouched) setSlug(slugify(next));
  };

  // Events carry no topics or keywords; the title and teaser are the brief.
  const coverContext: CoverContext = {
    slug,
    title,
    excerpt: summary,
    topics: [],
    keywords: [],
  };

  const submit = (submitEvent: FormEvent) => {
    submitEvent.preventDefault();
    if (
      slugInvalid ||
      slug.length === 0 ||
      endInvalid ||
      externalPairInvalid ||
      streamUrlInvalid ||
      capacityInvalid
    )
      return;
    onSubmit({
      slug,
      locale,
      title: title.trim(),
      summary: summary.trim() || null,
      body,
      // datetime-local only displays minute precision. Preserve untouched wire values
      // exactly so editing a title cannot erase seconds, microseconds or its offset.
      starts_at:
        initial && !startsAtChanged
          ? initial.starts_at
          : fromDateTimeLocal(startsAt),
      ends_at:
        initial && !endsAtChanged
          ? initial.ends_at
          : endsAt
            ? fromDateTimeLocal(endsAt)
            : null,
      location: location.trim() || null,
      // The API validates these as one pair. Sending both on every save also means
      // clearing a persisted action cannot leave a half-configured member CTA behind.
      external_url: trimmedExternalUrl || null,
      external_kind: trimmedExternalUrl ? externalKind : null,
      cover_image_url: coverImageUrl.trim() || null,
      stream_url: trimmedStreamUrl || null,
      registration_enabled: registrationEnabled,
      registration_capacity: capacityNumber,
      recording_slug: recordingSlug,
      is_published: isPublished,
      reminders_enabled: remindersEnabled,
    });
  };

  // The same expression the payload uses, so an untouched start previews as itself
  // instead of as a phantom edit: `toDateTimeLocal` drops the seconds a saved
  // timestamp carries, and round-tripping it would always read as a change.
  const scheduledFrom = (() => {
    if (initial && !startsAtChanged) return initial.starts_at;
    const parsed = new Date(startsAt);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  })();

  const previewDateOptions = {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  } as const;

  return (
    <Box
      component="form"
      onSubmit={submit}
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 7fr) minmax(0, 5fr)" },
        gap: { xs: 4, md: 6 },
        alignItems: "start",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField
          select
          label={t("eventEditor.fields.locale")}
          value={locale}
          onChange={(event) => setLocale(event.target.value)}
          slotProps={{ htmlInput: { "data-testid": "event.locale" } }}
          sx={{ maxWidth: 240 }}
        >
          {LOCALES.map((option) => (
            <MenuItem key={option} value={option}>
              {LOCALE_LABELS[option]}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label={t("eventEditor.fields.title")}
          value={title}
          onChange={(event) => changeTitle(event.target.value)}
          required
          fullWidth
          slotProps={{
            htmlInput: { "data-testid": "event.title", maxLength: 255 },
          }}
        />

        <TextField
          label={t("eventEditor.fields.slug")}
          value={slug}
          onChange={(event) => {
            setSlugTouched(true);
            setSlug(event.target.value);
          }}
          required
          fullWidth
          error={slugInvalid}
          helperText={t(
            reservedSlug
              ? "eventEditor.fields.slugReserved"
              : "eventEditor.fields.slugHelper",
          )}
          slotProps={{
            htmlInput: { "data-testid": "event.slug", maxLength: 160 },
          }}
        />

        <TextField
          label={t("eventEditor.fields.summary")}
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          multiline
          minRows={2}
          fullWidth
          helperText={t("eventEditor.fields.summaryHelper")}
          slotProps={{
            htmlInput: { "data-testid": "event.summary", maxLength: 500 },
          }}
        />

        <TextField
          label={t("eventEditor.fields.body")}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          required
          multiline
          minRows={10}
          fullWidth
          helperText={t("eventEditor.fields.bodyHelper")}
          slotProps={{ htmlInput: { "data-testid": "event.body" } }}
        />

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            label={t("eventEditor.fields.startsAt")}
            type="datetime-local"
            value={startsAt}
            onChange={(event) => {
              setStartsAtChanged(true);
              setStartsAt(event.target.value);
            }}
            required
            error={endInvalid}
            slotProps={{
              htmlInput: { "data-testid": "event.starts-at" },
              inputLabel: { shrink: true },
            }}
            sx={{ flex: "1 1 250px" }}
          />
          <TextField
            label={t("eventEditor.fields.endsAt")}
            type="datetime-local"
            value={endsAt}
            onChange={(event) => {
              setEndsAtChanged(true);
              setEndsAt(event.target.value);
            }}
            error={endInvalid}
            helperText={
              endInvalid ? t("eventEditor.fields.endsAtInvalid") : " "
            }
            slotProps={{
              htmlInput: {
                "data-testid": "event.ends-at",
                min: startsAt || undefined,
              },
              inputLabel: { shrink: true },
            }}
            sx={{ flex: "1 1 250px" }}
          />
        </Box>

        <TextField
          label={t("eventEditor.fields.location")}
          value={location}
          onChange={(event) => setLocation(event.target.value)}
          fullWidth
          helperText={t("eventEditor.fields.locationHelper")}
          slotProps={{
            htmlInput: { "data-testid": "event.location", maxLength: 255 },
          }}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
            gap: 2,
          }}
        >
          <TextField
            label={t("eventEditor.fields.externalUrl")}
            type="url"
            value={externalUrl}
            onChange={(event) => setExternalUrl(event.target.value)}
            fullWidth
            error={externalPairInvalid}
            helperText={t(
              externalPairInvalid
                ? "eventEditor.fields.externalPairInvalid"
                : "eventEditor.fields.externalUrlHelper",
            )}
            slotProps={{
              htmlInput: {
                "data-testid": "event.external-url",
                maxLength: 1024,
              },
            }}
          />
          <TextField
            select
            label={t("eventEditor.fields.externalKind")}
            value={externalKind ?? ""}
            onChange={(event) =>
              setExternalKind(
                event.target.value
                  ? (event.target.value as EventExternalKind)
                  : null,
              )
            }
            error={externalPairInvalid}
            helperText={externalPairInvalid ? " " : undefined}
            slotProps={{
              htmlInput: { "data-testid": "event.external-kind" },
            }}
          >
            <MenuItem value="">{t("eventEditor.externalKind.none")}</MenuItem>
            <MenuItem value="register">
              {t("eventEditor.externalKind.register")}
            </MenuItem>
            <MenuItem value="stream">
              {t("eventEditor.externalKind.stream")}
            </MenuItem>
          </TextField>
        </Box>

        <CoverField
          value={coverImageUrl}
          onChange={setCoverImageUrl}
          label={t("eventEditor.fields.coverImageUrl")}
          helperText={t("eventEditor.fields.coverImageUrlHelper")}
          inputTestId="event.cover"
          testIdPrefix="event"
          disabled={busy}
          busy={covers?.busy}
          onUpload={
            covers &&
            ((file, options) => covers.upload(file, options, coverContext))
          }
          onGenerate={
            covers && ((subject) => covers.generate(subject, coverContext))
          }
        />

        <TextField
          label={t("eventEditor.fields.streamUrl")}
          type="url"
          value={streamUrl}
          onChange={(event) => setStreamUrl(event.target.value)}
          fullWidth
          error={streamUrlInvalid}
          helperText={t("eventEditor.fields.streamUrlHelper")}
          slotProps={{
            htmlInput: { "data-testid": "event.stream-url", maxLength: 1024 },
          }}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
            gap: 2,
            alignItems: "start",
          }}
        >
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={registrationEnabled}
                  onChange={(event) =>
                    setRegistrationEnabled(event.target.checked)
                  }
                  data-testid="event.registration-enabled"
                />
              }
              label={t("eventEditor.fields.registrationEnabled")}
            />
            <Typography variant="T3" sx={{ color: "text.muted" }}>
              {t("eventEditor.fields.registrationEnabledHelper")}
            </Typography>
          </Box>
          <TextField
            label={t("eventEditor.fields.registrationCapacity")}
            type="number"
            value={registrationCapacity}
            onChange={(event) => setRegistrationCapacity(event.target.value)}
            disabled={!registrationEnabled}
            error={capacityInvalid}
            helperText={t("eventEditor.fields.registrationCapacityHelper")}
            slotProps={{
              htmlInput: {
                "data-testid": "event.registration-capacity",
                min: 1,
                max: 100000,
                step: 1,
              },
              inputLabel: { shrink: true },
            }}
          />
        </Box>

        <RecordingPicker
          locale={locale}
          value={recordingSlug}
          onChange={setRecordingSlug}
          disabled={busy}
        />

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={isPublished}
                onChange={(event) => setIsPublished(event.target.checked)}
                data-testid="event.is-published"
              />
            }
            label={t("eventEditor.fields.isPublished")}
          />
          <Typography variant="T3" sx={{ color: "text.muted" }}>
            {t("eventEditor.fields.isPublishedHelper")}
          </Typography>
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={remindersEnabled}
                onChange={(event) => setRemindersEnabled(event.target.checked)}
                data-testid="event.reminders-enabled"
              />
            }
            label={t("eventEditor.fields.remindersEnabled")}
          />
          <Typography variant="T3" sx={{ color: "text.muted" }}>
            {t("eventEditor.fields.remindersEnabledHelper")}
          </Typography>
        </Box>

        {errorKey ? (
          <Alert severity="error" data-testid="event.error">
            {t(errorKey)}
          </Alert>
        ) : null}

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={busy}
            data-testid="event.submit"
          >
            {submitLabel}
          </Button>
          {actions}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          position: { md: "sticky" },
          top: { md: 24 },
        }}
      >
        {aside}

        {/* Only a saved event has a ledger: the schedule is derived server-side from a
            row that exists, so there is nothing to show while creating one. */}
        {initial ? (
          <EventReminders
            reminders={initial.reminders}
            remindersEnabled={remindersEnabled}
            localeDrift={initial.locale_drift}
            startsAt={scheduledFrom}
            isPublished={isPublished}
          />
        ) : null}

        <Box>
          <Typography variant="OV1" sx={{ color: "text.muted" }}>
            {t("eventEditor.preview")}
          </Typography>
          <Typography variant="T3" sx={{ color: "text.muted", marginTop: 0.5 }}>
            {t("eventEditor.previewHint")}
          </Typography>
        </Box>
        <Box
          data-testid="event.preview"
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: `${RADIUS.lg}px`,
            padding: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflow: "hidden",
          }}
        >
          {coverImageUrl.trim() ? (
            <Box
              component="img"
              src={coverImageUrl.trim()}
              alt=""
              sx={{
                width: "100%",
                aspectRatio: "16 / 9",
                objectFit: "cover",
                borderRadius: `${RADIUS.md}px`,
              }}
            />
          ) : null}
          <Typography variant="H2" sx={{ color: "text.primary" }}>
            {title}
          </Typography>
          {startsAt ? (
            <Typography variant="L1" sx={{ color: "text.secondary" }}>
              {formatDate(fromDateTimeLocal(startsAt), previewDateOptions)}
            </Typography>
          ) : null}
          <Typography variant="T3" sx={{ color: "text.muted" }}>
            {location.trim() || t("events.online")}
          </Typography>
          {summary.trim() ? (
            <Typography variant="H3" sx={{ color: "text.secondary" }}>
              {summary}
            </Typography>
          ) : null}
          <Markdown>{body}</Markdown>
        </Box>
      </Box>
    </Box>
  );
}
